import { useRef, useState } from 'react';

import loadable from '@loadable/component';
import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  withLargeButtonAlignment,
  withRtlSupport,
} from '@skyscanner-internal/backpack-web/bpk-component-icon';
import BpkLargeMapIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/map';
import MinusIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/minus';
import PlusIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/plus';
import LandmarkIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/landmark';
import BpkMap, {
  BpkOverlayView,
  withGoogleMapsScript,
  BpkIconMarker,
} from '@skyscanner-internal/backpack-web/bpk-component-map';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import {
  type MapMarker,
  type DynamicMapProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DynamicMapProps';
import { redirect } from 'saddlebag-browser';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { useMBDContext } from '../common/MBD/useMBDContext';
import NoneStyleButton from '../common/NoneStyleButton';

import Locations from './Locations/Locations';
import MapDesktopMarker from './MarkerWrapper/MapDesktopMarker';
import MapMobileMarker from './MarkerWrapper/MapMobileMarker';
import MARKERS_DATA_SOURCE from './types';

import type { MapRef } from '@skyscanner-internal/backpack-web/bpk-component-map/src/BpkMap';

import STYLES from './DynamicMap.module.scss';

const AlignedBpkLargeMapIcon = withLargeButtonAlignment(BpkLargeMapIcon);
const AlignedLandmarkIconSm = withRtlSupport(LandmarkIconSm);

const cls = cssModules(STYLES);

const BpkMapWithScript =
  typeof window === 'undefined' ? BpkMap : withGoogleMapsScript(BpkMap);

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const getBounds = (entities: MapMarker[]) => {
  const lats = entities.map((item) => item.latitude).sort((a, b) => a - b);
  const lons = entities.map((item) => item.longitude).sort((a, b) => a - b);
  const bounds = {
    south: lats[0],
    west: lons[0],
    north: lats[lats.length - 1],
    east: lons[lons.length - 1],
  };
  return bounds;
};

const DynamicMap = (props: DynamicMapProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const {
    center,
    ctaButtonUrl,
    enableCtaButton,
    entities,
    googleMapsApiKey,
    locations,
    mapId,
    priceMarkerVersion = 'v2',
    strings,
    subTitle,
    title,
    zoomLevel,
  } = props;

  // Use zoom if it's provided and either:
  // - there's only one entity
  // - there's a center point
  const isSingleEntity = entities.length === 1;
  const shouldUseZoom = !!zoomLevel && (isSingleEntity || center);

  // If using zoom, skip bounds;
  const bounds = shouldUseZoom ? null : getBounds(entities);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef | null>(null);
  const [zoom, setZoom] = useState(zoomLevel);

  const handleMapRef = (mapInstance: MapRef) => {
    mapRef.current = mapInstance;
  };

  let centerPoint = center;

  // set the point in center, when there's only one entity
  if (entities.length === 1 && !center) {
    centerPoint = {
      latitude: entities[0].latitude,
      longitude: entities[0].longitude,
    };
  }

  const onClickZoomIn = () => {
    const currentZoom = mapRef.current?.getZoom();
    if (currentZoom !== undefined) {
      setZoom(currentZoom + 1);
    }
  };

  const onClickZoomOut = () => {
    const currentZoom = mapRef.current?.getZoom();
    if (currentZoom !== undefined) {
      setZoom(currentZoom - 1);
    }
  };

  const onClickCTA = () => {
    redirect(ctaButtonUrl);
  };

  const [selectedIndex, setSelectedIndex] = useState(-1);

  return (
    <>
      <div className={STYLES.DynamicMap__header}>
        <BpkSectionHeader title={title} description={subTitle} />
      </div>
      <div
        className={STYLES.DynamicMap__container}
        id="map-container"
        ref={mapContainerRef}
        data-tracking-element-id={rawComponentName}
        data-tracking-common-params={buildMBDParam(rawComponentName)}
      >
        <BpkMapWithScript
          googleMapsApiKey={googleMapsApiKey}
          bounds={bounds}
          center={centerPoint}
          showControls={false}
          panEnabled
          mapId={mapId}
          zoom={zoom}
          mapRef={handleMapRef}
          className={cls('DynamicMap__map', 'DynamicMap__hideDefaultControl')}
        >
          <div
            className={cls(
              'DynamicMap__controlContainer',
              'DynamicMap__viewModeControlContainer',
            )}
          >
            <div className={STYLES.DynamicMap__controlIconContainer}>
              <NoneStyleButton
                aria-label="extend the range"
                onClick={() => {
                  onClickZoomIn();
                }}
                data-tracking-element-id={`${rawComponentName}_zoom_in`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_zoom_in`,
                  { action: 'zoom_in' },
                )}
              >
                <span className={STYLES.DynamicMap__controlIcon}>
                  <PlusIcon />
                </span>
              </NoneStyleButton>
            </div>
            <div className={STYLES.DynamicMap__divider} />
            <div className={STYLES.DynamicMap__controlIconContainer}>
              <NoneStyleButton
                aria-label="narrow the range"
                onClick={() => {
                  onClickZoomOut();
                }}
                data-tracking-element-id={`${rawComponentName}_zoom_out`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_zoom_out`,
                  { action: 'zoom_out' },
                )}
              >
                <span className={STYLES.DynamicMap__controlIcon}>
                  <MinusIcon />
                </span>
              </NoneStyleButton>
            </div>
          </div>
          <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
            {(isMobile: boolean) => (
              <>
                {entities.map((location, index) => {
                  const position = {
                    latitude: location.latitude,
                    longitude: location.longitude,
                  };

                  if (
                    location.markersDataSource ===
                    MARKERS_DATA_SOURCE.POI_INFORMATION_FROM_HOTELS_INDICATIVE
                  ) {
                    return (
                      <>
                        <BpkIconMarker
                          icon={<AlignedLandmarkIconSm />}
                          position={position}
                        />
                        <BpkOverlayView position={position}>
                          <div className={STYLES.DynamicMap__IconText}>
                            <BpkText
                              textStyle={TEXT_STYLES.label3}
                              tagName="span"
                            >
                              {location.entityName}
                            </BpkText>
                          </div>
                        </BpkOverlayView>
                      </>
                    );
                  }

                  if (!isMobile) {
                    return (
                      <MapDesktopMarker
                        key={`mapMarker-wrapper-${location.entityName + index}`}
                        index={index}
                        strings={strings}
                        location={location}
                        priceMarkerVersion={priceMarkerVersion}
                      />
                    );
                  }

                  return (
                    <BpkOverlayView
                      key={`mapMarker-mobile-wrapper-${
                        location.entityName + index
                      }`}
                      position={position}
                    >
                      <div
                        className={STYLES.DynamicMap__tooltipContent}
                        id={`renderTargetId-${index}`}
                      >
                        <MapMobileMarker
                          key={`mapMarker${location.entityName + index}`}
                          renderTarget={() => mapContainerRef.current}
                          index={index}
                          selectedIndex={selectedIndex}
                          onSelected={() => setSelectedIndex(index)}
                          strings={strings}
                          location={location}
                          priceMarkerVersion={priceMarkerVersion}
                        />
                      </div>
                    </BpkOverlayView>
                  );
                })}
              </>
            )}
          </BpkBreakpoint>
        </BpkMapWithScript>
      </div>
      {Number(locations?.length) > 0 && (
        <Locations locations={locations || []} strings={strings} />
      )}
      {ctaButtonUrl && (
        <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
          {() =>
            enableCtaButton && (
              <div className={STYLES['DynamicMap__CTA-buttonWrapper']}>
                <BpkButton
                  aria-label={strings.ariaLabel}
                  className={STYLES['DynamicMap__CTA-button']}
                  onClick={onClickCTA}
                  data-tracking-element-id={`${rawComponentName}_cta`}
                  data-tracking-common-params={buildMBDParam(
                    `${rawComponentName}_cta`,
                    { action: 'cta_click' },
                  )}
                  type={BUTTON_TYPES.primaryOnLight}
                >
                  <AlignedBpkLargeMapIcon /> {strings.CTA}
                </BpkButton>
              </div>
            )
          }
        </BpkBreakpoint>
      )}
    </>
  );
};

export default (props: DynamicMapProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.DYNAMIC_MAP,
      })
    }
  >
    <DynamicMap {...props} />
  </IntersectionObserverWrapper>
);
